<template>
  <div class="terms-page">
    <div class="terms-container">
      <div class="terms-header">
        <router-link to="/" class="back-link">&larr; Back</router-link>
        <div class="lang-toggle">
          <button
            :class="{ active: lang === 'pt' }"
            @click="lang = 'pt'"
          >
            PT
          </button>
          <button
            :class="{ active: lang === 'en' }"
            @click="lang = 'en'"
          >
            EN
          </button>
        </div>
      </div>

      <!-- PORTUGUESE VERSION -->
      <article v-if="lang === 'pt'" class="terms-content">
        <h1>Termos e Condições</h1>
        <p class="last-updated">Última actualização: Março 2026</p>

        <section>
          <h2>1. Identificação do Prestador</h2>
          <p>
            <strong>Pátio Disciplinado — Unipessoal Lda</strong><br />
            NIF: 518 316 807<br />
            Sede: Incubadora A Praça, Rua dos Três Lagares, Nº 9, Antiga Praça
            Municipal, 6230-421 Fundão, Portugal
          </p>
        </section>

        <section>
          <h2>2. Âmbito dos Serviços</h2>
          <p>
            A Pátio Disciplinado presta serviços de design, desenvolvimento e
            gestão de soluções digitais, incluindo, quando contratados:
          </p>
          <ul>
            <li>
              <strong>Alojamento web (hosting)</strong> — disponibilização de
              espaço em servidor para websites e aplicações web;
            </li>
            <li>
              <strong>Registo e gestão de domínios</strong> — registo,
              renovação, transferência e configuração de nomes de domínio;
            </li>
            <li>
              <strong>Serviços de email profissional</strong> — criação e
              gestão de contas de email associadas ao domínio do cliente;
            </li>
            <li>
              <strong>Redirecionamento de domínio</strong> — configuração de
              redireccionamentos DNS;
            </li>
            <li>
              <strong>Consultoria e implementação técnica</strong> —
              configuração de infraestrutura digital.
            </li>
          </ul>
        </section>

        <section>
          <h2>3. Fornecedores de Infraestrutura (Terceiros)</h2>
          <p>
            A Pátio Disciplinado actua como <strong>intermediário</strong> na
            prestação de serviços de alojamento, domínios e email, utilizando
            as infraestruturas dos seguintes fornecedores:
          </p>
          <ul>
            <li>
              <strong>OVHcloud</strong> (OVH SAS) — alojamento web, cloud,
              registo de domínios e email profissional (Zimbra);
            </li>
            <li>
              <strong>Google</strong> (Google LLC) — Firebase (alojamento),
              Google Workspace (domínios e email) e Google Cloud Platform.
            </li>
          </ul>
          <p>
            Os serviços fornecidos através destes fornecedores estão sujeitos
            às respectivas condições gerais de serviço, disponíveis nos seus
            websites. A Pátio Disciplinado não é responsável por
            interrupções, limitações ou alterações de serviço impostas por
            estes fornecedores.
          </p>
        </section>

        <section>
          <h2>4. Obrigações do Cliente</h2>
          <ul>
            <li>
              Fornecer informações correctas e actualizadas para efeitos de
              registo de domínio e configuração de serviços;
            </li>
            <li>
              Não utilizar os serviços para fins ilegais, difamatórios, ou que
              violem direitos de terceiros;
            </li>
            <li>
              Manter as credenciais de acesso em segurança e comunicar de
              imediato qualquer acesso não autorizado;
            </li>
            <li>
              Efectuar os pagamentos acordados nos prazos estipulados.
            </li>
          </ul>
        </section>

        <section>
          <h2>5. Obrigações do Prestador</h2>
          <ul>
            <li>
              Configurar e manter os serviços contratados de forma diligente;
            </li>
            <li>
              Comunicar ao cliente qualquer interrupção planeada ou problema
              técnico relevante de que tome conhecimento;
            </li>
            <li>
              Respeitar a confidencialidade dos dados do cliente.
            </li>
          </ul>
        </section>

        <section>
          <h2>6. Disponibilidade e Limitação de Responsabilidade</h2>
          <p>
            A disponibilidade dos serviços de alojamento, domínios e email
            depende dos fornecedores terceiros identificados na cláusula 3. A
            Pátio Disciplinado envidará os melhores esforços para garantir a
            continuidade do serviço, mas não garante uma disponibilidade de
            100%.
          </p>
          <p>
            A Pátio Disciplinado não é responsável por:
          </p>
          <ul>
            <li>
              Perdas de dados resultantes de falhas dos fornecedores de
              infraestrutura;
            </li>
            <li>
              Interrupções causadas por manutenção planeada dos fornecedores;
            </li>
            <li>
              Danos indirectos, incluindo perda de receita ou oportunidades de
              negócio;
            </li>
            <li>
              Conteúdo publicado pelo cliente nos serviços contratados.
            </li>
          </ul>
        </section>

        <section>
          <h2>7. Protecção de Dados</h2>
          <p>
            A Pátio Disciplinado trata os dados pessoais dos seus clientes em
            conformidade com o Regulamento Geral sobre a Proteção de Dados
            (RGPD — Regulamento (UE) 2016/679). Os dados pessoais recolhidos
            são utilizados exclusivamente para a prestação e gestão dos
            serviços contratados.
          </p>
          <p>
            Os fornecedores terceiros operam ao abrigo dos seus próprios
            acordos de processamento de dados (DPA), nomeadamente o
            <em>OVH Data Protection Agreement</em> e os termos de
            processamento do Google Cloud.
          </p>
        </section>

        <section>
          <h2>8. Pagamentos e Renovações</h2>
          <ul>
            <li>
              Os valores e condições de pagamento são acordados por proposta ou
              contrato individual para cada cliente;
            </li>
            <li>
              A renovação de domínios e alojamento requer pagamento atempado.
              A não renovação pode resultar na perda do domínio ou suspensão
              do alojamento;
            </li>
            <li>
              Os custos dos fornecedores terceiros podem ser revistos
              anualmente e reflectidos nos valores cobrados ao cliente.
            </li>
          </ul>
        </section>

        <section>
          <h2>9. Cessação</h2>
          <p>
            Qualquer uma das partes pode cessar o contrato de serviço com um
            aviso prévio de 30 dias por escrito. Em caso de cessação:
          </p>
          <ul>
            <li>
              Será providenciada a transferência de domínios para o cliente ou
              novo prestador, mediante pedido;
            </li>
            <li>
              Os dados alojados serão mantidos durante 30 dias após a cessação
              para efeitos de migração;
            </li>
            <li>
              Não há reembolso de períodos de serviço já pagos e decorridos.
            </li>
          </ul>
        </section>

        <section>
          <h2>10. Lei Aplicável e Resolução de Litígios</h2>
          <p>
            Os presentes termos são regidos pela lei portuguesa. Para
            resolução de litígios, será competente o tribunal da comarca da
            sede do prestador, sem prejuízo do recurso a mecanismos de
            resolução alternativa de litígios.
          </p>
        </section>

        <section>
          <h2>11. Alterações aos Termos</h2>
          <p>
            A Pátio Disciplinado reserva-se o direito de actualizar estes
            termos, notificando os clientes com pelo menos 15 dias de
            antecedência. A utilização continuada dos serviços após a
            notificação constitui aceitação das alterações.
          </p>
        </section>
      </article>

      <!-- ENGLISH VERSION -->
      <article v-if="lang === 'en'" class="terms-content">
        <h1>Terms and Conditions</h1>
        <p class="last-updated">Last updated: March 2026</p>

        <section>
          <h2>1. Provider Identification</h2>
          <p>
            <strong>Pátio Disciplinado — Unipessoal Lda</strong><br />
            Tax ID (NIF): 518 316 807<br />
            Registered office: Incubadora A Praça, Rua dos Três Lagares, Nº 9,
            Antiga Praça Municipal, 6230-421 Fundão, Portugal
          </p>
        </section>

        <section>
          <h2>2. Scope of Services</h2>
          <p>
            Pátio Disciplinado provides design, development and management of
            digital solutions, including, when contracted:
          </p>
          <ul>
            <li>
              <strong>Web hosting</strong> — server space for websites and web
              applications;
            </li>
            <li>
              <strong>Domain registration and management</strong> —
              registration, renewal, transfer and configuration of domain
              names;
            </li>
            <li>
              <strong>Professional email services</strong> — creation and
              management of email accounts linked to the client's domain;
            </li>
            <li>
              <strong>Domain redirection</strong> — DNS redirect configuration;
            </li>
            <li>
              <strong>Technical consulting and implementation</strong> —
              digital infrastructure setup.
            </li>
          </ul>
        </section>

        <section>
          <h2>3. Infrastructure Providers (Third Parties)</h2>
          <p>
            Pátio Disciplinado acts as an <strong>intermediary</strong> in the
            provision of hosting, domain and email services, using the
            infrastructure of the following providers:
          </p>
          <ul>
            <li>
              <strong>OVHcloud</strong> (OVH SAS) — web hosting, cloud, domain
              registration and professional email (Zimbra);
            </li>
            <li>
              <strong>Google</strong> (Google LLC) — Firebase (hosting), Google
              Workspace (domains and email) and Google Cloud Platform.
            </li>
          </ul>
          <p>
            Services provided through these suppliers are subject to their
            respective general terms of service, available on their websites.
            Pátio Disciplinado is not responsible for interruptions,
            limitations or service changes imposed by these providers.
          </p>
        </section>

        <section>
          <h2>4. Client Obligations</h2>
          <ul>
            <li>
              Provide correct and up-to-date information for domain
              registration and service setup;
            </li>
            <li>
              Not use the services for illegal, defamatory purposes, or in
              ways that violate third-party rights;
            </li>
            <li>
              Keep access credentials secure and immediately report any
              unauthorised access;
            </li>
            <li>Make agreed payments within the stipulated deadlines.</li>
          </ul>
        </section>

        <section>
          <h2>5. Provider Obligations</h2>
          <ul>
            <li>
              Configure and maintain contracted services diligently;
            </li>
            <li>
              Inform the client of any planned interruptions or relevant
              technical issues;
            </li>
            <li>Respect the confidentiality of client data.</li>
          </ul>
        </section>

        <section>
          <h2>6. Availability and Limitation of Liability</h2>
          <p>
            The availability of hosting, domain and email services depends on
            the third-party providers identified in clause 3. Pátio
            Disciplinado will use its best efforts to ensure service
            continuity but does not guarantee 100% availability.
          </p>
          <p>Pátio Disciplinado is not liable for:</p>
          <ul>
            <li>
              Data loss resulting from infrastructure provider failures;
            </li>
            <li>
              Interruptions caused by provider scheduled maintenance;
            </li>
            <li>
              Indirect damages, including loss of revenue or business
              opportunities;
            </li>
            <li>Content published by the client on the contracted services.</li>
          </ul>
        </section>

        <section>
          <h2>7. Data Protection</h2>
          <p>
            Pátio Disciplinado processes its clients' personal data in
            accordance with the General Data Protection Regulation (GDPR —
            Regulation (EU) 2016/679). Personal data collected is used
            exclusively for the provision and management of contracted
            services.
          </p>
          <p>
            Third-party providers operate under their own data processing
            agreements (DPA), namely the
            <em>OVH Data Protection Agreement</em> and Google Cloud's data
            processing terms.
          </p>
        </section>

        <section>
          <h2>8. Payments and Renewals</h2>
          <ul>
            <li>
              Payment amounts and conditions are agreed upon through
              individual proposals or contracts for each client;
            </li>
            <li>
              Domain and hosting renewals require timely payment.
              Non-renewal may result in domain loss or hosting suspension;
            </li>
            <li>
              Third-party provider costs may be reviewed annually and
              reflected in charges to the client.
            </li>
          </ul>
        </section>

        <section>
          <h2>9. Termination</h2>
          <p>
            Either party may terminate the service agreement with 30 days'
            written notice. Upon termination:
          </p>
          <ul>
            <li>
              Domain transfers to the client or a new provider will be
              facilitated upon request;
            </li>
            <li>
              Hosted data will be retained for 30 days after termination for
              migration purposes;
            </li>
            <li>
              No refunds are issued for service periods already paid for and
              elapsed.
            </li>
          </ul>
        </section>

        <section>
          <h2>10. Governing Law and Dispute Resolution</h2>
          <p>
            These terms are governed by Portuguese law. For the resolution of
            disputes, the court of the provider's registered office shall have
            jurisdiction, without prejudice to alternative dispute resolution
            mechanisms.
          </p>
        </section>

        <section>
          <h2>11. Changes to Terms</h2>
          <p>
            Pátio Disciplinado reserves the right to update these terms,
            notifying clients at least 15 days in advance. Continued use of
            services after notification constitutes acceptance of the changes.
          </p>
        </section>
      </article>
    </div>

    <!-- Footer -->
    <SiteFooter />
  </div>
</template>

<script>
import SiteFooter from "@/components/SiteFooter.vue";

export default {
  name: "TermsView",
  components: {
    SiteFooter,
  },
  data() {
    return {
      lang: "pt",
    };
  },
};
</script>

<style scoped>
.terms-page {
  min-height: 100vh;
  background-color: var(--bg-main-dark);
  color: var(--text-white);
  display: flex;
  flex-direction: column;
}

.terms-container {
  flex: 1;
  max-width: 720px;
  margin: 0 auto;
  padding: var(--doubleSpace);
  padding-top: calc(var(--doubleSpace) * 2);
}

.terms-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--doubleSpace);
}

.back-link {
  color: var(--text-white);
  text-decoration: none;
  font-size: var(--largeFontSize);
  opacity: 0.6;
  transition: opacity var(--fastSpeed);
}

.back-link:hover {
  opacity: 1;
}

.lang-toggle {
  display: flex;
  gap: var(--smallSpace);
}

.lang-toggle button {
  height: var(--doubleLargeSpace);
  padding: 0 var(--space);
  font-size: var(--smallFontSize);
}

.lang-toggle button.active {
  border-color: var(--text-white);
  opacity: 1;
}

.lang-toggle button:not(.active) {
  opacity: 0.4;
}

.terms-content h1 {
  font-size: calc(var(--largeFontSize) * 1.6);
  margin-bottom: var(--space);
}

.last-updated {
  opacity: 0.5;
  font-size: var(--smallFontSize);
  margin-bottom: var(--doubleSpace);
}

.terms-content section {
  margin-bottom: var(--doubleSpace);
}

.terms-content h2 {
  font-size: calc(var(--largeFontSize) * 1.05);
  margin-bottom: var(--space);
  color: var(--light-pink);
}

.terms-content p {
  font-size: var(--regularFontSize);
  line-height: 1.7;
  margin-bottom: var(--space);
  opacity: 0.85;
}

.terms-content ul {
  padding-left: var(--doubleSpace);
  margin-bottom: var(--space);
}

.terms-content li {
  font-size: var(--regularFontSize);
  line-height: 1.7;
  margin-bottom: var(--smallSpace);
  opacity: 0.85;
}

.terms-content strong {
  color: var(--text-white);
}

.terms-content em {
  font-style: italic;
  opacity: 0.7;
}
</style>
